/**
 * React Query hooks for the runs endpoints (RLY-024).
 *
 * Standing-order [25] + Sprint-04 retro [PROCESS]: explicit `useQuery` generic
 * declarations; companion `*-shape.test.ts` ships `expectTypeOf` assertions to
 * lock the inferred shapes against drift.
 */
import { useAuth } from "@clerk/clerk-react";
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ApiError,
  ListRunsResponse,
  RunCostEventsResponse,
  RunResponse,
  TriggerRunRequest,
  TriggerRunResponse,
} from "@/lib/api-client";
import { fetchRun, fetchRunCostEvents, fetchRuns, triggerRun } from "@/lib/api-client";

export const runsKeys = {
  all: ["runs"] as const,
  list: (params: { agentId?: string; limit?: number; cursor?: string } = {}) =>
    ["runs", "list", params] as const,
  detail: (runId: string) => ["runs", "detail", runId] as const,
  costEvents: (runId: string) => ["runs", "detail", runId, "cost-events"] as const,
} as const;

export type RunsListQueryKey = ReturnType<typeof runsKeys.list>;
export type RunDetailQueryKey = ReturnType<typeof runsKeys.detail>;
export type RunCostEventsQueryKey = ReturnType<typeof runsKeys.costEvents>;

// ─── List ─────────────────────────────────────────────────────────────────

export type UseRunsListResult = UseQueryResult<ListRunsResponse, ApiError | Error>;

export function useRunsList(
  params: { agentId?: string; limit?: number; cursor?: string } = {},
): UseRunsListResult {
  const { getToken } = useAuth();
  return useQuery<ListRunsResponse, ApiError | Error, ListRunsResponse, RunsListQueryKey>({
    queryKey: runsKeys.list(params),
    queryFn: () =>
      fetchRuns({
        getToken: () => getToken(),
        ...(params.agentId !== undefined ? { agentId: params.agentId } : {}),
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
        ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
      }),
  });
}

// ─── Detail ───────────────────────────────────────────────────────────────

export type UseRunDetailResult = UseQueryResult<RunResponse, ApiError | Error>;

/**
 * Terminal lifecycle states for a run. A run in any of these states will not
 * change again, so live polling stops once we observe one. Mirrors the
 * `RunStatus['state']` enum in `@simpliwork/executor-core`.
 */
const TERMINAL_STATUSES: ReadonlySet<RunResponse["status"]> = new Set([
  "completed",
  "failed",
  "cancelled",
  "timeout",
]);

export function isTerminalRunStatus(status: RunResponse["status"]): boolean {
  return TERMINAL_STATUSES.has(status);
}

export interface UseRunDetailOptions {
  /**
   * Polling interval in milliseconds when the run is in a non-terminal state
   * (`pending` or `running`). When the run reaches a terminal state, polling
   * stops automatically. Defaults to 1000ms per RLY-025 AC #2 (BUILD_PLAN.md
   * M1.9 — SSE upgrade is Phase 3).
   */
  pollIntervalMs?: number;
}

export function useRunDetail(
  runId: string | undefined,
  options: UseRunDetailOptions = {},
): UseRunDetailResult {
  const { getToken } = useAuth();
  const pollIntervalMs = options.pollIntervalMs ?? 1000;
  return useQuery<RunResponse, ApiError | Error, RunResponse, RunDetailQueryKey>({
    queryKey: runsKeys.detail(runId ?? ""),
    queryFn: () => {
      if (!runId) throw new Error("runId is required");
      return fetchRun(runId, { getToken: () => getToken() });
    },
    enabled: Boolean(runId),
    // RLY-025 AC #2: poll while in a non-terminal state, stop when terminal.
    // TanStack Query lets `refetchInterval` return `false` to disable polling
    // dynamically — we use that to stop as soon as the response shows a
    // terminal status, without an extra effect or state machine.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return pollIntervalMs;
      return isTerminalRunStatus(data.status) ? false : pollIntervalMs;
    },
  });
}

// ─── Cost Events (terminal-only, one-shot) ────────────────────────────────

export type UseRunCostEventsResult = UseQueryResult<RunCostEventsResponse, ApiError | Error>;

/**
 * Fetch the finalized cost events for a run. Gated on
 * `isTerminalRunStatus(runStatus)` — pending/running runs don't have
 * finalized cost events yet, and the run-detail page polls the run row
 * separately. No `refetchInterval` here; the status flip drives refetch.
 */
export function useRunCostEvents(
  runId: string | undefined,
  runStatus: RunResponse["status"] | undefined,
): UseRunCostEventsResult {
  const { getToken } = useAuth();
  return useQuery<RunCostEventsResponse, ApiError | Error, RunCostEventsResponse, RunCostEventsQueryKey>(
    {
      queryKey: runsKeys.costEvents(runId ?? ""),
      queryFn: () => {
        if (!runId) throw new Error("runId is required");
        return fetchRunCostEvents(runId, { getToken: () => getToken() });
      },
      enabled:
        Boolean(runId) && runStatus !== undefined && isTerminalRunStatus(runStatus),
    },
  );
}

// ─── Trigger ──────────────────────────────────────────────────────────────

export type UseTriggerRunResult = UseMutationResult<
  TriggerRunResponse,
  ApiError | Error,
  { agentId: string; body: TriggerRunRequest }
>;

export function useTriggerRun(): UseTriggerRunResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    TriggerRunResponse,
    ApiError | Error,
    { agentId: string; body: TriggerRunRequest }
  >({
    mutationFn: ({ agentId, body }) => triggerRun(agentId, body, { getToken: () => getToken() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: runsKeys.all });
    },
  });
}
